/**
 * Data Validation Layer
 * Validates team names, match fixtures, and match statistics
 * Cross-checks AI-extracted stats against expected ranges
 */

// ============================================
// TEAM NAME VALIDATION & CLEANING
// ============================================

/**
 * Cleans a team name by removing common scraping artifacts
 */
export function cleanTeamName(name: string | null | undefined): string {
  if (!name || typeof name !== 'string') return '';
  let cleaned = name.trim();
  
  // Remove "Full Highlights" suffix (common scraping artifact)
  cleaned = cleaned.replace(/Full Highlights$/i, '').trim();
  
  // Remove minute markers (e.g., "86' William Gomes", "75' Martim Fernandes")
  cleaned = cleaned.replace(/\d+['′]\s*[A-Za-z\s]+$/, '').trim();
  
  // Remove leading numbers and dots (position indicators)
  cleaned = cleaned.replace(/^[0-9]+\.?\s*/, '');
  
  // Remove leading special characters
  cleaned = cleaned.replace(/^[!#@*]+\s*/, '');
  
  // Remove 'br' HTML line break artifacts (e.g., "!JuventusbrJuventus" -> "Juventus")
  const brMatch = cleaned.match(/^!?([A-Za-z\s]+)br([A-Za-z\s]+)$/);
  if (brMatch) {
    cleaned = brMatch[2]!; // Use the second occurrence (after 'br')
  }
  
  // Remove trailing artifacts
  cleaned = cleaned.replace(/\s*(qualification|play-off|relegation|promotion|Video|Highlights).*$/i, '');
  
  return cleaned.trim();
}

/**
 * Validates that a team name is a proper team name and not corrupted data
 * (e.g., league standings, positions, or scraping artifacts)
 */
export function isValidTeamName(name: string | null | undefined): boolean {
  if (!name || typeof name !== 'string') return false;
  const trimmed = name.trim();
  
  // Team name must not be empty and have reasonable length
  if (trimmed === '' || trimmed.length < 2 || trimmed.length > 100) return false;
  
  // Team name should not start with a number (league position indicator)
  if (/^[0-9]/.test(trimmed)) return false;
  
  // Team name should not start with special characters
  if (/^!/.test(trimmed)) return false;
  
  // Team name should not contain 'br' HTML formatting artifacts
  if (/br[A-Z]/.test(trimmed)) return false;
  
  // Team name should not contain HTML entities or tags
  if (/<[^>]+>/.test(trimmed) || /&[a-z]+;/.test(trimmed)) return false;
  
  // Team name should not contain URLs
  if (/https?:\/\//.test(trimmed) || /www\./.test(trimmed)) return false;
  
  // Team name must contain at least one letter
  if (!/[a-zA-Z]/.test(trimmed)) return false;
  
  // Team name should not contain 'vs' as a standalone (indicates fixture format error)
  if (/\s+vs\s+/.test(trimmed)) return false;
  
  // Team name should not contain score patterns (e.g., "2-1", "3:0")
  if (/^\d+\s*[-:]\s*\d+$/.test(trimmed)) return false;
  
  // Scraping artifacts
  if (/Full Highlights/i.test(trimmed)) return false;
  if (/\d+['′]\s*[A-Za-z]/.test(trimmed)) return false; // Minute markers like "86' Player"
  if (/Video/i.test(trimmed)) return false;
  
  // UI/Poll artifacts from scraping
  const invalidPatterns = [
    /who will win/i,
    /cast your vote/i,
    /advertisement/i,
    /download the app/i,
    /featured odds/i,
    /compare teams/i,
    /top players/i,
    /smooth, easy/i,
    /tournament planning/i,
    /animations/i,
    /spot the assist/i,
    /uefa ranking/i,
    /fifa ranking/i,
  ];
  
  for (const pattern of invalidPatterns) {
    if (pattern.test(trimmed)) return false;
  }
  
  // League names that sometimes appear as team names (scraping error)
  const leaguePatterns = [
    /^A-League/i,
    /Première Ligue/i,
    /Frauen-Bundesliga/i,
    /^Premier League$/i,
    /^Ligue 1$/i,
    /^Serie A$/i,
    /^La Liga$/i,
    /^Bundesliga$/i,
    /OFB Cup/i,
    /Copa Chile/i,
    /Primera B$/i,
    /Stars League$/i,
    /Challenger Pro League$/i,
  ];
  
  for (const pattern of leaguePatterns) {
    if (pattern.test(trimmed)) return false;
  }
  
  // Competition keywords that indicate standings, not teams
  const invalidKeywords = [
    'Champions League', 'Europa League', 'Conference League',
    'Championship', 'Highlights', 'Fixtures', 'Results', 'Table',
    'play-off', 'qualification', 'Relegation', 'Promotion', 'UEFA',
    'Standings', 'Statistics', 'News', 'Transfers', 'Live Score',
    'Tickets', 'Shop'
  ];
  
  for (const keyword of invalidKeywords) {
    if (trimmed.toLowerCase().includes(keyword.toLowerCase())) return false;
  }
  
  return true;
}

/**
 * Validates a complete match fixture to ensure it's a real match
 */
export function isValidMatchFixture(
  homeTeam: string | null | undefined,
  awayTeam: string | null | undefined,
  leagueOrKickoffTime?: string | null
): boolean {
  // Both team names must be valid
  if (!isValidTeamName(homeTeam) || !isValidTeamName(awayTeam)) {
    return false;
  }
  
  // Teams should not be the same
  if (homeTeam && awayTeam && homeTeam.trim().toLowerCase() === awayTeam.trim().toLowerCase()) {
    return false;
  }
  
  // If kickoff time provided (ISO format), validate it
  if (leagueOrKickoffTime && /^\d{4}-\d{2}-\d{2}/.test(leagueOrKickoffTime)) {
    try {
      const kickoff = new Date(leagueOrKickoffTime);
      if (isNaN(kickoff.getTime())) return false;
    } catch {
      return false;
    }
  }
  
  return true;
}

/**
 * Validates a match fixture with detailed error reporting
 */
export function validateMatchFixture(
  homeTeam: string | null | undefined,
  awayTeam: string | null | undefined,
  league: string | null | undefined
): { valid: boolean; errors: string[] } {
  const errors: string[] = [];
  
  if (!isValidTeamName(homeTeam)) {
    errors.push(`Invalid home team: "${homeTeam}"`);
  }
  
  if (!isValidTeamName(awayTeam)) {
    errors.push(`Invalid away team: "${awayTeam}"`);
  }
  
  // Teams should not be the same
  if (homeTeam && awayTeam && homeTeam.trim().toLowerCase() === awayTeam.trim().toLowerCase()) {
    errors.push('Home and away teams are identical');
  }
  
  // League validation
  if (!league || league.trim().length < 2) {
    errors.push('Missing or invalid league');
  }
  
  return {
    valid: errors.length === 0,
    errors
  };
}

/**
 * Validates a prediction record for data integrity
 */
export function validatePredictionData(prediction: {
  team_home?: string | null;
  team_away?: string | null;
  league?: string | null;
  predicted_outcome?: string | null;
  odds?: number | null;
  confidence_score?: number | null;
}): { valid: boolean; errors: string[] } {
  const errors: string[] = [];
  
  if (!isValidTeamName(prediction.team_home)) {
    errors.push(`Invalid team_home: "${prediction.team_home}" - appears to be standings or corrupted data`);
  }
  
  if (!isValidTeamName(prediction.team_away)) {
    errors.push(`Invalid team_away: "${prediction.team_away}" - appears to be standings or corrupted data`);
  }
  
  if (!prediction.league || prediction.league.trim() === '') {
    errors.push('Missing league');
  }
  
  if (!prediction.predicted_outcome || prediction.predicted_outcome.trim() === '') {
    errors.push('Missing predicted_outcome');
  }
  
  if (prediction.odds === null || prediction.odds === undefined || prediction.odds < 1) {
    errors.push(`Invalid odds: ${prediction.odds}`);
  }
  
  if (prediction.confidence_score === null || prediction.confidence_score === undefined || 
      prediction.confidence_score < 0 || prediction.confidence_score > 100) {
    errors.push(`Invalid confidence_score: ${prediction.confidence_score}`);
  }
  
  return {
    valid: errors.length === 0,
    errors
  };
}

// ============================================
// MATCH STATS VALIDATION
// ============================================

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
  correctedData?: Partial<MatchStats> | undefined;
}

export interface MatchStats {
  possession_home?: number | null;
  possession_away?: number | null;
  shots_home?: number | null;
  shots_away?: number | null;
  shots_on_target_home?: number | null;
  shots_on_target_away?: number | null;
  corners_home?: number | null;
  corners_away?: number | null;
  fouls_home?: number | null;
  fouls_away?: number | null;
  home_yellow_cards?: number | null;
  away_yellow_cards?: number | null;
  home_red_cards?: number | null;
  away_red_cards?: number | null;
  first_half_home_score?: number | null;
  first_half_away_score?: number | null;
  home_score?: number | null;
  away_score?: number | null;
  attendance?: number | null;
}

/**
 * Validate possession stats - must sum to approximately 100%
 */
function validatePossession(stats: MatchStats): { errors: string[]; warnings: string[]; corrected?: Partial<MatchStats> | undefined } {
  const errors: string[] = [];
  const warnings: string[] = [];
  let corrected: Partial<MatchStats> | undefined;

  if (stats.possession_home !== null && stats.possession_home !== undefined &&
      stats.possession_away !== null && stats.possession_away !== undefined) {
    
    const total = stats.possession_home + stats.possession_away;
    
    // Must be between 0-100 each
    if (stats.possession_home < 0 || stats.possession_home > 100) {
      errors.push(`Invalid home possession: ${stats.possession_home} (must be 0-100)`);
    }
    if (stats.possession_away < 0 || stats.possession_away > 100) {
      errors.push(`Invalid away possession: ${stats.possession_away} (must be 0-100)`);
    }
    
    // Total should sum to ~100 (allow 95-105 for rounding)
    if (total < 95 || total > 105) {
      errors.push(`Possession sum invalid: ${total}% (expected ~100%)`);
    } else if (total !== 100) {
      warnings.push(`Possession sum is ${total}%, normalizing to 100%`);
      const homeNormalized = Math.round((stats.possession_home / total) * 100);
      corrected = {
        possession_home: homeNormalized,
        possession_away: 100 - homeNormalized
      };
    }
  }

  return { errors, warnings, corrected };
}

/**
 * Validate shots - shots on target must be <= total shots
 */
function validateShots(stats: MatchStats): { errors: string[]; warnings: string[]; corrected?: Partial<MatchStats> | undefined } {
  const errors: string[] = [];
  const warnings: string[] = [];
  let corrected: Partial<MatchStats> | undefined;

  // Home team shots validation
  if (stats.shots_home !== null && stats.shots_home !== undefined &&
      stats.shots_on_target_home !== null && stats.shots_on_target_home !== undefined) {
    
    if (stats.shots_on_target_home > stats.shots_home) {
      errors.push(`Home shots on target (${stats.shots_on_target_home}) > total shots (${stats.shots_home})`);
      corrected = { ...corrected, shots_on_target_home: stats.shots_home };
    }
    
    if (stats.shots_home < 0) errors.push(`Invalid home shots: ${stats.shots_home}`);
    if (stats.shots_on_target_home < 0) errors.push(`Invalid home shots on target: ${stats.shots_on_target_home}`);
  }

  // Away team shots validation
  if (stats.shots_away !== null && stats.shots_away !== undefined &&
      stats.shots_on_target_away !== null && stats.shots_on_target_away !== undefined) {
    
    if (stats.shots_on_target_away > stats.shots_away) {
      errors.push(`Away shots on target (${stats.shots_on_target_away}) > total shots (${stats.shots_away})`);
      corrected = { ...corrected, shots_on_target_away: stats.shots_away };
    }
    
    if (stats.shots_away < 0) errors.push(`Invalid away shots: ${stats.shots_away}`);
    if (stats.shots_on_target_away < 0) errors.push(`Invalid away shots on target: ${stats.shots_on_target_away}`);
  }

  // Reasonable range check
  if (stats.shots_home !== null && stats.shots_home !== undefined && stats.shots_home > 40) {
    warnings.push(`Unusually high home shots: ${stats.shots_home}`);
  }
  if (stats.shots_away !== null && stats.shots_away !== undefined && stats.shots_away > 40) {
    warnings.push(`Unusually high away shots: ${stats.shots_away}`);
  }

  return { errors, warnings, corrected };
}

/**
 * Validate half-time scores against full-time scores
 */
function validateScores(stats: MatchStats): { errors: string[]; warnings: string[] } {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (stats.first_half_home_score !== null && stats.first_half_home_score !== undefined &&
      stats.home_score !== null && stats.home_score !== undefined) {
    if (stats.first_half_home_score > stats.home_score) {
      errors.push(`HT home score (${stats.first_half_home_score}) > FT home score (${stats.home_score})`);
    }
  }

  if (stats.first_half_away_score !== null && stats.first_half_away_score !== undefined &&
      stats.away_score !== null && stats.away_score !== undefined) {
    if (stats.first_half_away_score > stats.away_score) {
      errors.push(`HT away score (${stats.first_half_away_score}) > FT away score (${stats.away_score})`);
    }
  }

  const scores = [stats.home_score, stats.away_score, stats.first_half_home_score, stats.first_half_away_score];
  scores.forEach((score) => {
    if (score !== null && score !== undefined) {
      if (score < 0) errors.push(`Invalid score value: ${score} (must be >= 0)`);
      if (score > 15) warnings.push(`Unusually high score: ${score}`);
    }
  });

  return { errors, warnings };
}

/**
 * Validate cards
 */
function validateCards(stats: MatchStats): { errors: string[]; warnings: string[] } {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (stats.home_yellow_cards !== null && stats.home_yellow_cards !== undefined) {
    if (stats.home_yellow_cards < 0) errors.push(`Invalid home yellow cards: ${stats.home_yellow_cards}`);
    if (stats.home_yellow_cards > 11) warnings.push(`Unusually high home yellow cards: ${stats.home_yellow_cards}`);
  }

  if (stats.away_yellow_cards !== null && stats.away_yellow_cards !== undefined) {
    if (stats.away_yellow_cards < 0) errors.push(`Invalid away yellow cards: ${stats.away_yellow_cards}`);
    if (stats.away_yellow_cards > 11) warnings.push(`Unusually high away yellow cards: ${stats.away_yellow_cards}`);
  }

  if (stats.home_red_cards !== null && stats.home_red_cards !== undefined) {
    if (stats.home_red_cards < 0) errors.push(`Invalid home red cards: ${stats.home_red_cards}`);
    if (stats.home_red_cards > 5) errors.push(`Unusually high home red cards: ${stats.home_red_cards}`);
  }

  if (stats.away_red_cards !== null && stats.away_red_cards !== undefined) {
    if (stats.away_red_cards < 0) errors.push(`Invalid away red cards: ${stats.away_red_cards}`);
    if (stats.away_red_cards > 5) errors.push(`Unusually high away red cards: ${stats.away_red_cards}`);
  }

  return { errors, warnings };
}

/**
 * Validate corners and fouls
 */
function validateCornersAndFouls(stats: MatchStats): { errors: string[]; warnings: string[] } {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (stats.corners_home !== null && stats.corners_home !== undefined) {
    if (stats.corners_home < 0) errors.push(`Invalid home corners: ${stats.corners_home}`);
    if (stats.corners_home > 20) warnings.push(`Unusually high home corners: ${stats.corners_home}`);
  }

  if (stats.corners_away !== null && stats.corners_away !== undefined) {
    if (stats.corners_away < 0) errors.push(`Invalid away corners: ${stats.corners_away}`);
    if (stats.corners_away > 20) warnings.push(`Unusually high away corners: ${stats.corners_away}`);
  }

  if (stats.fouls_home !== null && stats.fouls_home !== undefined) {
    if (stats.fouls_home < 0) errors.push(`Invalid home fouls: ${stats.fouls_home}`);
    if (stats.fouls_home > 35) warnings.push(`Unusually high home fouls: ${stats.fouls_home}`);
  }

  if (stats.fouls_away !== null && stats.fouls_away !== undefined) {
    if (stats.fouls_away < 0) errors.push(`Invalid away fouls: ${stats.fouls_away}`);
    if (stats.fouls_away > 35) warnings.push(`Unusually high away fouls: ${stats.fouls_away}`);
  }

  return { errors, warnings };
}

/**
 * Validate attendance
 */
function validateAttendance(stats: MatchStats): { errors: string[]; warnings: string[] } {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (stats.attendance !== null && stats.attendance !== undefined) {
    if (stats.attendance < 0) errors.push(`Invalid attendance: ${stats.attendance}`);
    if (stats.attendance > 110000) warnings.push(`Unusually high attendance: ${stats.attendance}`);
  }

  return { errors, warnings };
}

/**
 * Main validation function - validates all stats and returns comprehensive result
 */
export function validateMatchStats(stats: MatchStats): ValidationResult {
  const allErrors: string[] = [];
  const allWarnings: string[] = [];
  let correctedData: Partial<MatchStats> = {};

  const possessionResult = validatePossession(stats);
  allErrors.push(...possessionResult.errors);
  allWarnings.push(...possessionResult.warnings);
  if (possessionResult.corrected) correctedData = { ...correctedData, ...possessionResult.corrected };

  const shotsResult = validateShots(stats);
  allErrors.push(...shotsResult.errors);
  allWarnings.push(...shotsResult.warnings);
  if (shotsResult.corrected) correctedData = { ...correctedData, ...shotsResult.corrected };

  const scoresResult = validateScores(stats);
  allErrors.push(...scoresResult.errors);
  allWarnings.push(...scoresResult.warnings);

  const cardsResult = validateCards(stats);
  allErrors.push(...cardsResult.errors);
  allWarnings.push(...cardsResult.warnings);

  const cornersResult = validateCornersAndFouls(stats);
  allErrors.push(...cornersResult.errors);
  allWarnings.push(...cornersResult.warnings);

  const attendanceResult = validateAttendance(stats);
  allErrors.push(...attendanceResult.errors);
  allWarnings.push(...attendanceResult.warnings);

  return {
    isValid: allErrors.length === 0,
    errors: allErrors,
    warnings: allWarnings,
    correctedData: Object.keys(correctedData).length > 0 ? correctedData : undefined
  };
}

/**
 * Validate and auto-correct stats where possible
 */
export function validateAndCorrectStats(stats: MatchStats): { stats: MatchStats; validation: ValidationResult } {
  const validation = validateMatchStats(stats);
  
  if (validation.correctedData) {
    return {
      stats: { ...stats, ...validation.correctedData },
      validation
    };
  }
  
  return { stats, validation };
}
